package com.evoapps.evoshift.domain.spreadsheet

/**
 * A CSV/TSV file, or cells pasted from a spreadsheet, as rows of cells. The separator is guessed
 * from the first lines (a tab as Excel and Google Sheets copy cells, `;` as Polish Excel saves
 * CSV, or `,`); quoted cells may hold separators, line breaks and `""`. Blank lines stay as empty
 * rows, so row numbers are the file's lines.
 */
object CsvReader {

    /** The text of [bytes] ([TextDecoding]) as one [Sheet]. */
    fun read(bytes: ByteArray, maxRows: Int = SpreadsheetReader.MAX_ROWS): Sheet = Sheet("", rows(TextDecoding.decode(bytes), maxRows))

    /** Rows of [text]; more than [maxRows] rows fail with [SpreadsheetProblem.TOO_LARGE]. */
    fun rows(text: String, maxRows: Int = SpreadsheetReader.MAX_ROWS): List<List<String>> {
        val content = text.removePrefix("﻿")
        val separator = separatorOf(content)
        val rows = mutableListOf<List<String>>()
        var row = mutableListOf<String>()
        val cell = StringBuilder()
        var quoted = false
        var i = 0
        fun endCell() { row += cell.toString(); cell.clear() }
        fun endRow() {
            endCell()
            rows += if (row.any { it.isNotBlank() }) row else emptyList()
            row = mutableListOf()
            if (rows.size > maxRows) throw SpreadsheetException(SpreadsheetProblem.TOO_LARGE, "more than $maxRows rows")
        }
        while (i < content.length) {
            val c = content[i]
            when {
                quoted && c == '"' && i + 1 < content.length && content[i + 1] == '"' -> { cell.append('"'); i++ }
                c == '"' && (quoted || cell.isBlank()) -> { if (!quoted) cell.clear(); quoted = !quoted }
                quoted -> cell.append(c)
                c == separator -> endCell()
                c == '\r' -> Unit
                c == '\n' -> endRow()
                else -> cell.append(c)
            }
            i++
        }
        if (cell.isNotEmpty() || row.isNotEmpty()) endRow()
        // The trailing blank lines of a file are not rows.
        while (rows.isNotEmpty() && rows.last().isEmpty()) rows.removeAt(rows.lastIndex)
        return rows
    }

    /**
     * The separator used most in the first lines, outside quotes. A tab wins when every line has
     * one (pasted cells); `;` beats `,` on a tie (decimal commas).
     */
    fun separatorOf(text: String): Char {
        val sample = text.lineSequence().filter { it.isNotBlank() }.take(10).toList()
        fun count(separator: Char) = sample.sumOf { line ->
            var quoted = false
            line.count { c -> if (c == '"') { quoted = !quoted; false } else !quoted && c == separator }
        }
        if (sample.isNotEmpty() && sample.all { '\t' in it }) return '\t'
        return listOf(';', '\t', ',').maxByOrNull { count(it) * 10 + if (it == ';') 1 else 0 } ?: ';'
    }
}

/**
 * The text of a CSV file: UTF-8 (with or without a byte order mark) when the bytes are valid
 * UTF-8, UTF-16 with a byte order mark (Excel's "Unicode text"), else Windows-1250, which older
 * Polish Excel versions use for "CSV (separated by semicolons)". Every platform decodes the same
 * way (the table is here, not in a platform charset).
 */
object TextDecoding {

    fun decode(bytes: ByteArray): String {
        if (bytes.size >= 2 && bytes[0] == 0xFF.toByte() && bytes[1] == 0xFE.toByte()) return utf16(bytes, littleEndian = true)
        if (bytes.size >= 2 && bytes[0] == 0xFE.toByte() && bytes[1] == 0xFF.toByte()) return utf16(bytes, littleEndian = false)
        if (bytes.any { it == 0.toByte() }) throw SpreadsheetException(SpreadsheetProblem.CORRUPT, "binary data")
        return try {
            bytes.decodeToString(throwOnInvalidSequence = true).removePrefix("﻿")
        } catch (e: CharacterCodingException) {
            windows1250(bytes)
        }
    }

    private fun utf16(bytes: ByteArray, littleEndian: Boolean): String {
        val out = StringBuilder(bytes.size / 2)
        var i = 2
        while (i + 1 < bytes.size) {
            val a = bytes[i].toInt() and 0xFF
            val b = bytes[i + 1].toInt() and 0xFF
            out.append((if (littleEndian) a or (b shl 8) else (a shl 8) or b).toChar())
            i += 2
        }
        return out.toString()
    }

    /** Windows-1250 (Central European): ASCII, then [CP1250_HIGH] for 0x80-0xFF. */
    fun windows1250(bytes: ByteArray): String = buildString(bytes.size) {
        for (b in bytes) {
            val v = b.toInt() and 0xFF
            append(if (v < 0x80) v.toChar() else CP1250_HIGH[v - 0x80])
        }
    }

    /** 0x80-0xFF of Windows-1250; the five unassigned bytes are U+FFFD. */
    private const val CP1250_HIGH =
        "€�‚�„…†‡�‰Š‹ŚŤŽŹ" +
            "�‘’“”•–—�™š›śťžź" +
            " ˇ˘Ł¤Ą¦§¨©Ş«¬­®Ż" +
            "°±˛ł´µ¶·¸ąş»Ľ˝ľż" +
            "ŔÁÂĂÄĹĆÇČÉĘËĚÍÎĎ" +
            "ĐŃŇÓÔŐÖ×ŘŮÚŰÜÝŢß" +
            "ŕáâăäĺćçčéęëěíîď" +
            "đńňóôőö÷řůúűüýţ˙"
}
